import { useEffect } from 'react'
import { Alert, Button, Descriptions, Form, Input, Modal, Select, Space } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { SiteSelect } from '@/features/sites/SiteSelect'
import { EquipmentOwnerRef } from './EquipmentOwnerRef'
import { WarehouseSelect } from './WarehouseSelect'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  EQUIPMENT_TYPES,
  createEquipment,
  updateEquipment,
  type Equipment,
  type EquipmentType,
} from './equipmentApi'

interface EquipmentFormModalProps {
  open: boolean
  /** `null` = yangi jihoz */
  equipment: Equipment | null
  onClose: () => void
  /**
   * Egani boshqarish oynasini ochish (XSB-003).
   *
   * ⚠️ EGA BU FORMANING MAYDONI EMAS va bo'la ham olmaydi: `PUT` tanasi uni
   * umuman qabul qilmaydi (`EquipmentUpdate`), ega esa ALOHIDA amal —
   * `POST /assign` holat mashinasidan o'tadi (`IN_STOCK -> ASSIGNED`) va
   * audit jurnaliga `EQUIPMENT_ASSIGN` bo'lib alohida yoziladi. Uni
   * "Saqlash" tugmasi ostiga qo'shish ikkalasi bitta tranzaksiyada
   * bajarilyaptidek ko'rsatardi — model va nom o'zgardi-yu, ega
   * o'zgarmadi degan holat jimgina yuz berardi.
   *
   * Shuning uchun bu yerda maydon emas, KO'RSATKICH: joriy ega ko'rinadi
   * va uni o'zgartiradigan joyga tugma olib boradi. Tekshiruvchi aynan shu
   * oynada qidirgan edi — bo'sh qoldirish uni yana qidirishga majburlardi.
   */
  onChangeOwner?: () => void
}

interface FormValues {
  type: EquipmentType
  serialNumber: string
  model?: string
  notes?: string
  siteId?: string | null
  nodeId?: string | null
}

/**
 * Jihoz qo'shish va tahrirlash.
 *
 * ⚠️ IKKI HAR XIL SO'ROV, bitta forma (`NasFormModal` bilan bir xil andoza):
 * yaratishda `type` va `serialNumber` beriladi, tahrirlashda esa ular
 * MAYDON SIFATIDA UMUMAN YO'Q — `PUT` tanasi faqat `model`, `notes`, `siteId`.
 *
 * Sabab `equipmentApi.EquipmentDraft` da: seriya raqami jihozning o'zligi.
 * Uni tahrirlash "boshqa qurilma" degani bo'lardi, tarix esa (kimga berilgan,
 * qachon buzilgan) eski qurilmaga bog'langan holda qolardi. Shuning uchun
 * ular tahrirlashda o'qish uchun ko'rsatiladi, o'zgartirib bo'lmaydi.
 *
 * <h2>⚠️ OMBOR (`nodeId`) — 2026-08-13 da qo'shildi, sababi o'lchangan</h2>
 * Bu maydon formada UMUMAN yo'q edi va oqibati ikki tomonlama:
 *
 *   · `PUT` tanasi `nodeId` siz ketardi, Jackson uni `null` qilardi va eski
 *     qoidada (to'liq almashtirish) omborni JIMGINA o'chirardi — jihoz
 *     ombordan chiqib ketardi, hech qanday xato bermasdi. Backend buni
 *     `COALESCE(:nodeId, node_id)` bilan yopdi;
 *   · yaratishda esa `nodeId` tor doira (`SUBTREE`) uchun MAJBURIY
 *     (`EquipmentServiceImpl#requireTargetNodeInScope`), maydon esa yo'q edi
 *     — ya'ni filial boshlig'i panel orqali jihoz ro'yxatga OLA olmasdi.
 *
 * ⚠️ Ombor va joylashuv — IKKI HAR XIL savol va ikkalasi ham kerak:
 * joylashuv FIZIK joy ("Chorsu binosi"), ombor esa TASHKILIY birlik ("Mazar
 * ombori"). Bitta binoda ikki filialning ombori turishi mumkin va aksincha
 * (`Equipment` javadoc'i). Shuning uchun ular ikkita alohida maydon.
 */
export function EquipmentFormModal({
  open,
  equipment,
  onClose,
  onChangeOwner,
}: EquipmentFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      equipment
        ? {
            type: equipment.type,
            serialNumber: equipment.serialNumber,
            model: equipment.model ?? '',
            notes: equipment.notes ?? '',
            siteId: equipment.siteId,
            // ⚠️ JORIY ombor formaga QAYTADAN qo'yiladi va saqlashda
            // qaytariladi. Bu majburiy: maydon bo'sh ochilsa, tegilmagan
            // holda ham `null` yuborilardi va operator "men omborga
            // tegmadim" deb o'ylagan tahrir ombordan chiqarib yuborardi
            // (aynan shu nuqson bugun jonli o'lchangan).
            nodeId: equipment.nodeId,
          }
        : {
            type: 'ROUTER',
            serialNumber: '',
            model: '',
            notes: '',
            siteId: null,
            nodeId: null,
          },
    )
  }, [open, equipment, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // Bo'sh satr `null` ga aylantiriladi — bo'sh katak "to'ldirilgan"
      // bo'lib ko'rinmasin
      const common = {
        model: values.model?.trim() || null,
        notes: values.notes?.trim() || null,
        siteId: values.siteId ?? null,
        // ⚠️ HAR DOIM yuboriladi — tahrirda o'zgarmagan bo'lsa ham.
        // Tushib qolgan maydon `PUT` da `null` bo'lib boradi va u
        // "tegma" degani (`EquipmentRepository#updateDetails`), ya'ni
        // ombor KO'CHIRISH jimgina bajarilmay qolardi.
        nodeId: values.nodeId ?? null,
      }
      return equipment
        ? updateEquipment(equipment.id, common)
        : createEquipment({
            ...common,
            type: values.type,
            serialNumber: values.serialNumber.trim(),
          })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={equipment ? t('inventory.editTitle') : t('inventory.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        {equipment ? (
          <>
            {/* Uzun izoh — matn qisqarmaydi, faqat ikki qatordan ortig'i
                «Batafsil» ostiga tushadi (XSB-004) */}
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('inventory.editNote')}
            />
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('inventory.serialNumber')}>
                {equipment.serialNumber}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.type')}>
                {t(`equipmentType.${equipment.type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory.owner')}>
                <Space size={8} wrap>
                  <EquipmentOwnerRef
                    ownerType={equipment.ownerType}
                    ownerId={equipment.ownerId}
                    showHint={false}
                  />
                  {onChangeOwner && (equipment.state === 'IN_STOCK' || equipment.state === 'ASSIGNED') ? (
                    <Can permission="inventory.equipment.write">
                      <Button size="small" type="link" style={{ padding: 0 }} onClick={onChangeOwner}>
                        {equipment.state === 'ASSIGNED'
                          ? t('inventory.ownerChange')
                          : t('inventory.ownerAssign')}
                      </Button>
                    </Can>
                  ) : null}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <>
            <Form.Item
              name="type"
              label={t('inventory.type')}
              extra={t('inventory.typeHint')}
              rules={[{ required: true, message: t('inventory.validation.typeRequired') }]}
            >
              <Select<EquipmentType>
                options={EQUIPMENT_TYPES.map((value) => ({
                  value,
                  label: t(`equipmentType.${value}`),
                }))}
              />
            </Form.Item>

            <Form.Item
              name="serialNumber"
              label={t('inventory.serialNumber')}
              extra={t('inventory.serialNumberHint')}
              rules={[{ required: true, message: t('inventory.validation.serialRequired') }]}
            >
              <Input autoFocus placeholder="SN-000123" />
            </Form.Item>
          </>
        )}

        <Form.Item name="model" label={t('inventory.model')} extra={t('inventory.modelHint')}>
          <Input placeholder={t('common.noValue')} />
        </Form.Item>

        {/* ⚠️ Joylashuv — inventarning eng muhim maydoni: "bu qurilma qayerda"
            degan savolga faqat shu javob beradi. Seriya raqami buni aytmaydi. */}
        <Form.Item name="siteId" label={t('inventory.site')} extra={t('inventory.siteHint')}>
          <SiteSelect />
        </Form.Item>

        {/* ⚠️ Ombor — hisob o'lchovi, joylashuvdan BOSHQA savol (yuqoridagi
            izoh). Tor doirali foydalanuvchi uchun u yaratishda MAJBURIY:
            omborsiz jihoz uni yaratgan odamga ham ko'rinmay qoladi. */}
        <Form.Item
          name="nodeId"
          label={t('inventory.warehouse')}
          extra={equipment ? t('inventory.warehouseEditHint') : t('inventory.warehouseHint')}
        >
          {/* Tahrirlashda tozalash tugmasi YO'Q — `PUT` da `null` "tozala"
              emas, "tegma" degani (WarehouseSelect.allowClear izohi) */}
          <WarehouseSelect allowClear={equipment === null} />
        </Form.Item>

        <Form.Item name="notes" label={t('inventory.notes')}>
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
